/* MAT TRY ON — [04] native product-details panel preview */
(() => {
  if (window.__mat04NativePanelBooted) return;
  window.__mat04NativePanelBooted = true;

  const configCache = new Map();
  const state = new WeakMap();
  const pendingFitEntries = new Map();

  const getLocal = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
  const setLocal = (key, value) => { try { localStorage.setItem(key, value); } catch {} };

  function shopperId() {
    let id = getLocal('mat:sid');
    if (!id) {
      id = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      setLocal('mat:sid', id);
    }
    return id;
  }

  async function shopperHash(sid, shop) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(shop + ':' + sid));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
  }

  function trackMatEvent(panel, kind, extra = {}) {
    if (!panel?.dataset?.appUrl || !panel?.dataset?.shopDomain) return;
    const ctx = state.get(panel) || {};
    fetch(panel.dataset.appUrl + '/api/event', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        shopDomain: panel.dataset.shopDomain,
        kind,
        productId: panel.dataset.productId || '',
        tryOnId: ctx.tryOnId || '',
        entryPoint: ctx.entryPoint || 'pdp',
        selectedSize: ctx.fitProfile?.selectedSize || ctx.fitSeed?.selectedSize || '',
        targetCategory: ctx.targetCategory || '',
        ...extra,
      }),
    }).catch(() => {});
  }

  async function getJSON(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let data;
    try { data = JSON.parse(text); } catch { data = { error: text }; }
    if (!res.ok) throw Object.assign(new Error(data.error || 'Request failed'), { status: res.status, data });
    return data;
  }

  async function normalizePhoto(file) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const maxSide = 2600;
      const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, width, height);
      bitmap.close?.();

      const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Could not normalize photo')), type, type === 'image/jpeg' ? 0.94 : undefined);
      });
      const base = String(file.name || 'photo').replace(/\.[^.]+$/, '');
      return new File([blob], base + (type === 'image/png' ? '.png' : '.jpg'), { type, lastModified: Date.now() });
    } catch {
      return file;
    }
  }

  function drawRegionPath(ctx, region, width, height) {
    const points = Array.isArray(region?.polygon) ? region.polygon : [];
    if (points.length < 3) return false;
    ctx.beginPath();
    points.forEach((point, index) => {
      const x = Math.max(0, Math.min(1, Number(point.x) || 0)) * width;
      const y = Math.max(0, Math.min(1, Number(point.y) || 0)) * height;
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    return true;
  }

  async function compositeLockedResult(originalFile, generatedUrl, editRegion, protectedRegions = []) {
    const original = await createImageBitmap(originalFile, { imageOrientation: 'from-image' });
    const generatedBlob = await fetch(generatedUrl, { mode: 'cors' }).then((res) => {
      if (!res.ok) throw new Error('Could not load generated image');
      return res.blob();
    });
    const generated = await createImageBitmap(generatedBlob);

    try {
      const width = original.width;
      const height = original.height;
      if (!width || !height || !editRegion?.polygon?.length) throw new Error('MAT could not establish a safe edit region');

      const mask = document.createElement('canvas');
      mask.width = width;
      mask.height = height;
      const mctx = mask.getContext('2d');
      if (!mctx) throw new Error('Mask unavailable');
      mctx.fillStyle = '#fff';
      if (!drawRegionPath(mctx, editRegion, width, height)) throw new Error('Invalid MAT edit region');
      mctx.fill();

      mctx.globalCompositeOperation = 'destination-out';
      for (const region of protectedRegions || []) {
        if (!drawRegionPath(mctx, region, width, height)) continue;
        mctx.fill();
      }
      mctx.globalCompositeOperation = 'source-over';

      const feathered = document.createElement('canvas');
      feathered.width = width;
      feathered.height = height;
      const fmctx = feathered.getContext('2d');
      if (!fmctx) throw new Error('Mask unavailable');
      fmctx.filter = 'blur(' + Math.max(1.5, Math.min(5, width / 500)) + 'px)';
      fmctx.drawImage(mask, 0, 0);

      const generatedLayer = document.createElement('canvas');
      generatedLayer.width = width;
      generatedLayer.height = height;
      const gctx = generatedLayer.getContext('2d');
      if (!gctx) throw new Error('Canvas unavailable');
      gctx.drawImage(generated, 0, 0, width, height);
      gctx.globalCompositeOperation = 'destination-in';
      gctx.drawImage(feathered, 0, 0);
      gctx.globalCompositeOperation = 'source-over';

      const output = document.createElement('canvas');
      output.width = width;
      output.height = height;
      const out = output.getContext('2d');
      if (!out) throw new Error('Canvas unavailable');
      out.drawImage(original, 0, 0);
      out.drawImage(generatedLayer, 0, 0);

      return await new Promise((resolve, reject) => {
        output.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Could not finalize protected try-on')), 'image/jpeg', 0.94);
      });
    } finally {
      original.close?.();
      generated.close?.();
    }
  }

  async function uploadSelfie(appUrl, shopDomain, file) {
    const { key, uploadUrl } = await postJSON(appUrl + '/api/upload-url', { shopDomain, contentType: file.type });
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': file.type },
      body: file,
    });
    if (!put.ok) throw new Error('Upload failed');
    return key;
  }

  async function getConfig(el) {
    const sid = shopperId();
    const hash = await shopperHash(sid, el.dataset.shopDomain);
    const themeKey = el.dataset.matThemeKey || '';
    const cacheKey = el.dataset.shopDomain + ':' + el.dataset.productId + ':' + themeKey + ':' + hash;
    if (!configCache.has(cacheKey)) {
      const url = el.dataset.appUrl + '/api/config?shop=' + encodeURIComponent(el.dataset.shopDomain)
        + '&product=' + encodeURIComponent(el.dataset.productId)
        + '&themeKey=' + encodeURIComponent(themeKey)
        + '&h=' + hash;
      configCache.set(cacheKey, getJSON(url).then((cfg) => ({ cfg, hash })));
    }
    return configCache.get(cacheKey);
  }

  function applyTokens(el, cfg) {
    el.style.setProperty('--mat-accent', cfg.accent || '#808D45');
    el.style.setProperty('--mat-ink', cfg.ink || '#080808');
    el.style.setProperty('--mat-paper', cfg.paper || '#FFFFFF');
  }

  function launcherHTML() {
    return '<button type="button" class="mat-launcher" data-mat-open>Try on You</button>';
  }

  function step(panel, name) {
    panel.querySelectorAll('[data-mat-step]').forEach((el) => { el.hidden = el.dataset.matStep !== name; });
  }

  function panelHTML(cfg) {
    return '<div class="mat-flow">'
      + '<section data-mat-step="fit" hidden>'
      + '<div class="mat-flow__eyebrow"><span></span>SIZE ACCURACY</div>'
      + '<h3>Fit-aware preview</h3>'
      + '<p>You are trying <strong data-mat-fit-selected>this size</strong>. Add a few details so MAT can interpret the selected garment measurements more accurately.</p>'
      + '<form class="mat-fit-form" data-mat-fit-form>'
      + '<div class="mat-fit-grid">'
      + '<label class="mat-fit-field"><span>Height</span><div class="mat-fit-input"><input type="number" min="120" max="230" step="1" inputmode="numeric" placeholder="175" data-mat-height required><em>cm</em></div></label>'
      + '<label class="mat-fit-field"><span>Usual size</span><select data-mat-usual-size required><option value="">Select</option><option>XXS</option><option>XS</option><option>S</option><option>M</option><option>L</option><option>XL</option><option>XXL</option><option>3XL</option></select></label>'
      + '<label class="mat-fit-field mat-fit-field--optional"><span>Weight <small>optional</small></span><div class="mat-fit-input"><input type="number" min="35" max="220" step="1" inputmode="numeric" placeholder="70" data-mat-weight><em>kg</em></div></label>'
      + '</div>'
      + '<button type="submit" class="mat-flow__button mat-flow__button--accent">Continue to Try On</button>'
      + '<small class="mat-flow__note">Used for this preview only. Your photo remains the source of your body shape.</small>'
      + '</form>'
      + '</section>'

      + '<section data-mat-step="consent">'
      + '<div class="mat-flow__eyebrow"><span></span>MAT TRY ON</div>'
      + '<h3>' + (cfg.cta || 'Try it on you') + '</h3>'
      + '<p>' + (cfg.consent || '') + '</p>'
      + '<button type="button" class="mat-flow__button" data-mat-consent>Continue</button>'
      + '</section>'

      + '<section data-mat-step="upload" hidden>'
      + '<div class="mat-flow__eyebrow"><span></span>PHOTO</div>'
      + '<h3>Choose a clear photo</h3>'
      + '<p>Standing or seated is fine. Keep your face and the garment area clearly visible. Use the original photo when possible.</p>'
      + '<div class="mat-flow__photo-actions">'
      + '<label class="mat-flow__photo-choice mat-flow__photo-choice--primary">'
      + '<input type="file" accept="image/jpeg,image/png" capture="user" data-mat-file>'
      + '<strong>Take a photo</strong>'
      + '<small>Open camera</small>'
      + '</label>'
      + '<label class="mat-flow__photo-choice">'
      + '<input type="file" accept="image/jpeg,image/png" data-mat-file>'
      + '<strong>Choose from photos</strong>'
      + '<small>Use an existing image</small>'
      + '</label>'
      + '</div>'
      + '<small class="mat-flow__photo-note">JPG or PNG · max 7 MB</small>'
      + '</section>'

      + '<section data-mat-step="loading" hidden>'
      + '<div class="mat-flow__eyebrow"><span></span>PROCESSING</div>'
      + '<h3>Drawing the fit</h3>'
      + '<p>This usually takes a few seconds.</p>'
      + '<div class="mat-flow__spinner"></div>'
      + '</section>'

      + '<section data-mat-step="result" hidden>'
      + '<div class="mat-flow__eyebrow"><span></span>RESULT</div>'
      + '<h3>Here you are</h3>'
      + '<img class="mat-flow__result" data-mat-result crossorigin="anonymous" alt="Virtual try-on result">'
      + '<div class="mat-flow__actions mat-flow__actions--result">'
      + '<button type="button" class="mat-flow__button mat-flow__button--accent" data-mat-social>Create social card</button>'
      + '<button type="button" class="mat-flow__button mat-flow__button--ghost" data-mat-again>Try another photo</button>'
      + '</div>'
      + '<small class="mat-flow__note">Preview only. Fit may vary from the real garment.</small>'
      + '</section>'

      + '<section data-mat-step="lead" hidden>'
      + '<div class="mat-flow__eyebrow"><span></span>CONTINUE</div>'
      + '<h3>Keep trying</h3>'
      + '<p>Leave your email to continue with another try-on.</p>'
      + '<form data-mat-lead>'
      + '<input type="email" placeholder="you@email.com" data-mat-email required>'
      + '<label class="mat-flow__check"><input type="checkbox" data-mat-marketing> <span>I would like to hear about new arrivals.</span></label>'
      + '<button class="mat-flow__button" type="submit">Continue</button>'
      + '</form>'
      + '</section>'

      + '<section data-mat-step="error" hidden>'
      + '<div class="mat-flow__eyebrow"><span></span>TRY AGAIN</div>'
      + '<h3>Could not generate</h3>'
      + '<p data-mat-error></p>'
      + '<button type="button" class="mat-flow__button" data-mat-retry>Try again</button>'
      + '</section>'
      + '</div>';
  }

  async function hydrateLauncher(block) {
    try {
      const { cfg } = await getConfig(block);
      if (!cfg.productEnabled) {
        block.dataset.matEnabled = 'false';
        block.hidden = true;
        return;
      }
      block.hidden = false;
      block.dataset.matEnabled = 'true';
      applyTokens(block, cfg);
      block.innerHTML = launcherHTML(cfg);
    } catch {
      block.dataset.matEnabled = 'false';
      block.hidden = true;
    }
  }

  async function hydratePanel(panel) {
    try {
      const { cfg, hash } = await getConfig(panel);
      const row = panel.closest('details[data-testid="accordion-details"]');
      if (!cfg.productEnabled) {
        panel.dataset.matEnabled = 'false';
        panel.hidden = true;
        if (row) row.hidden = true;
        return;
      }
      panel.hidden = false;
      panel.dataset.matEnabled = 'true';
      if (row) row.hidden = false;
      applyTokens(panel, cfg);
      panel.innerHTML = panelHTML(cfg);
      state.set(panel, { cfg, hash, entryPoint: 'pdp' });
      wirePanel(panel);
      const pendingFit = pendingFitEntries.get(panel.dataset.productId || '');
      if (pendingFit) applyFitEntry(panel, pendingFit);
    } catch (error) {
      panel.dataset.matEnabled = 'false';
      panel.hidden = true;
      const row = panel.closest('details[data-testid="accordion-details"]');
      if (row) row.hidden = true;
    }
  }

  function applyFitEntry(panel, detail) {
    const ctx = state.get(panel);
    if (!ctx || !detail) return;

    ctx.entryPoint = 'size_chart';
    trackMatEvent(panel, 'size_accuracy_open');
    ctx.fitSeed = {
      selectedSize: String(detail.selectedSize || '').trim(),
      measurementUnit: detail.measurementUnit === 'in' ? 'in' : 'cm',
      measurements: detail.measurements && typeof detail.measurements === 'object' ? detail.measurements : {},
    };

    const selected = panel.querySelector('[data-mat-fit-selected]');
    if (selected) selected.textContent = ctx.fitSeed.selectedSize ? 'size ' + ctx.fitSeed.selectedSize : 'this size';

    const usual = panel.querySelector('[data-mat-usual-size]');
    if (usual && ctx.fitSeed.selectedSize) {
      const match = [...usual.options].find((option) => option.value.toLowerCase() === ctx.fitSeed.selectedSize.toLowerCase());
      if (match) usual.value = match.value;
    }

    step(panel, 'fit');
  }

  function submitFit(panel, event) {
    event.preventDefault();
    const ctx = state.get(panel);
    if (!ctx?.fitSeed) return;

    const heightCm = Number(panel.querySelector('[data-mat-height]')?.value || 0);
    const weightRaw = panel.querySelector('[data-mat-weight]')?.value || '';
    const weightKg = weightRaw ? Number(weightRaw) : null;
    const usualSize = String(panel.querySelector('[data-mat-usual-size]')?.value || '').trim();

    if (!Number.isFinite(heightCm) || heightCm < 120 || heightCm > 230 || !usualSize) {
      return showError(panel, 'Add a valid height and your usual size.');
    }
    if (weightKg != null && (!Number.isFinite(weightKg) || weightKg < 35 || weightKg > 220)) {
      return showError(panel, 'Weight is optional. If added, use a value between 35 and 220 kg.');
    }

    ctx.fitProfile = {
      heightCm,
      weightKg,
      usualSize,
      selectedSize: ctx.fitSeed.selectedSize,
      measurementUnit: ctx.fitSeed.measurementUnit,
      measurements: ctx.fitSeed.measurements,
    };
    step(panel, 'consent');
  }

  function wirePanel(panel) {
    panel.querySelector('[data-mat-fit-form]')?.addEventListener('submit', (event) => submitFit(panel, event));
    panel.querySelector('[data-mat-consent]')?.addEventListener('click', () => step(panel, 'upload'));
    panel.querySelector('[data-mat-again]')?.addEventListener('click', () => step(panel, 'upload'));
    panel.querySelector('[data-mat-retry]')?.addEventListener('click', () => step(panel, 'upload'));
    panel.querySelector('[data-mat-social]')?.addEventListener('click', () => createSocialCard(panel));
    panel.querySelectorAll('[data-mat-file]').forEach((input) => {
      input.addEventListener('change', (event) => handleFile(panel, event.target.files?.[0]));
    });
    panel.querySelector('[data-mat-lead]')?.addEventListener('submit', (event) => submitLead(panel, event));
  }

  async function handleFile(panel, file) {
    if (!file) return;
    if (!/^image\/(jpeg|png)$/.test(file.type)) return showError(panel, 'Use a JPG or PNG photo.');

    const ctx = state.get(panel);
    if (!ctx) return;
    step(panel, 'loading');
    trackMatEvent(panel, 'photo_upload');

    try {
      const normalizedFile = await normalizePhoto(file);
      if (normalizedFile.size > 7 * 1024 * 1024) return showError(panel, 'Photo is too large. Maximum 7 MB.');
      const key = await uploadSelfie(panel.dataset.appUrl, panel.dataset.shopDomain, normalizedFile);
      const result = await postJSON(panel.dataset.appUrl + '/api/try-on', {
        shopDomain: panel.dataset.shopDomain,
        productId: panel.dataset.productId,
        selfieKey: key,
        shopperHash: ctx.hash,
        themeKey: panel.dataset.matThemeKey || '',
        entryPoint: ctx.entryPoint || 'pdp',
        fitProfile: ctx.fitProfile || undefined,
      });
      if (ctx.localResultUrl) {
        URL.revokeObjectURL(ctx.localResultUrl);
        ctx.localResultUrl = '';
      }

      let displayedUrl = result.resultUrl;
      if (result.editRegion?.polygon?.length) {
        const protectedBlob = await compositeLockedResult(
          normalizedFile,
          result.resultUrl,
          result.editRegion,
          result.protectedRegions || [],
        );
        ctx.protectedResultBlob = protectedBlob;
        ctx.localResultUrl = URL.createObjectURL(protectedBlob);
        displayedUrl = ctx.localResultUrl;

        if (result.finalize?.uploadUrl && result.finalize?.key && result.tryOnId) {
          try {
            const put = await fetch(result.finalize.uploadUrl, {
              method: 'PUT',
              headers: { 'content-type': result.finalize.contentType || 'image/jpeg' },
              body: protectedBlob,
            });
            if (!put.ok) throw new Error('Final result upload failed');
            const finalized = await postJSON(panel.dataset.appUrl + '/api/finalize-result', {
              shopDomain: panel.dataset.shopDomain,
              tryOnId: result.tryOnId,
              shopperHash: ctx.hash,
              resultKey: result.finalize.key,
            });
            ctx.persistedResultUrl = finalized.resultUrl || '';
          } catch {
            ctx.persistedResultUrl = '';
          }
        }
      }

      ctx.rawResultUrl = result.resultUrl;
      ctx.resultUrl = displayedUrl;
      ctx.tryOnId = result.tryOnId || '';
      ctx.targetCategory = result.targetCategory || '';
      ctx.productTitle = result.productTitle || panel.dataset.productTitle || 'MAT TRY ON';
      panel.querySelector('[data-mat-result]').src = displayedUrl;
      step(panel, 'result');
      trackMatEvent(panel, 'tryon_result_view', { targetCategory: ctx.targetCategory });
      window.__mat04LastSuccessfulTryOn = { panel, productId: panel.dataset.productId, tryOnId: ctx.tryOnId, tracked: false };
      prepareSocialCard(panel).catch(() => {});
    } catch (error) {
      if (error.status === 402 && error.data?.requiresEmail) return step(panel, 'lead');
      showError(panel, error.data?.error || error.message || 'Try-on failed.');
    }
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not decode result image'));
      img.src = src;
    });
  }

  function slugify(value) {
    return String(value || 'mat-try-on')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 72) || 'mat-try-on';
  }

  function drawCover(ctx, img, x, y, width, height) {
    const scale = Math.max(width / img.width, height / img.height);
    const sourceW = width / scale;
    const sourceH = height / scale;
    const sourceX = Math.max(0, (img.width - sourceW) / 2);
    const sourceY = Math.max(0, (img.height - sourceH) / 2);
    ctx.drawImage(img, sourceX, sourceY, sourceW, sourceH, x, y, width, height);
  }

  function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
    const words = String(text).split(/\s+/);
    let line = '';
    let lineNo = 0;
    for (let i = 0; i < words.length && lineNo < maxLines; i++) {
      const test = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, y + lineNo * lineHeight);
        lineNo += 1;
        line = words[i];
      } else {
        line = test;
      }
    }
    if (line && lineNo < maxLines) ctx.fillText(line, x, y + lineNo * lineHeight);
  }

  function drawSocialCard(ctx, img, productTitle) {
    const W = 1080;
    const H = 1920;
    const moss = '#808D45';
    const ink = '#080808';
    const paper = '#F2F2F2';

    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = ink;
    ctx.fillRect(0, 0, W, 148);
    ctx.fillStyle = '#FFFFFF';
    ctx.textBaseline = 'middle';
    ctx.font = '600 34px Arial, Helvetica, sans-serif';
    ctx.fillText('[04]-STUDIOS™', 58, 74);
    ctx.textAlign = 'right';
    ctx.font = '500 24px Arial, Helvetica, sans-serif';
    ctx.fillText('MAT TRY ON / SEEN ON YOU', W - 58, 74);
    ctx.textAlign = 'left';

    drawCover(ctx, img, 0, 148, W, 1460);

    ctx.fillStyle = paper;
    ctx.fillRect(0, 1608, W, H - 1608);
    ctx.fillStyle = moss;
    ctx.fillRect(58, 1668, 12, 12);

    ctx.fillStyle = ink;
    ctx.font = '600 22px Arial, Helvetica, sans-serif';
    ctx.fillText('GENERATED FROM PRODUCT PAGE', 88, 1681);

    ctx.font = '600 44px Arial, Helvetica, sans-serif';
    drawWrappedText(ctx, String(productTitle || 'MAT TRY ON').toUpperCase(), 58, 1764, 820, 50, 2);

    ctx.font = '500 23px Arial, Helvetica, sans-serif';
    ctx.fillText('04-studios.com', 58, 1870);
    ctx.textAlign = 'right';
    ctx.font = '500 21px Arial, Helvetica, sans-serif';
    ctx.fillText('04 / HIDDEN GARDEN', W - 58, 1870);
    ctx.textAlign = 'left';
  }

  async function prepareSocialCard(panel) {
    const ctxState = state.get(panel);
    const button = panel.querySelector('[data-mat-social]');
    const img = panel.querySelector('[data-mat-result]');
    if (!ctxState?.resultUrl || !button || !img || ctxState.socialCardFile) return;

    button.disabled = true;
    button.textContent = 'Preparing social card…';

    try {
      if (!img.complete || !img.naturalWidth) {
        await new Promise((resolve, reject) => {
          const onLoad = () => { cleanup(); resolve(); };
          const onError = () => { cleanup(); reject(new Error('Could not load result image')); };
          const cleanup = () => {
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
          };
          img.addEventListener('load', onLoad, { once: true });
          img.addEventListener('error', onError, { once: true });
        });
      }

      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const canvasCtx = canvas.getContext('2d');
      if (!canvasCtx) throw new Error('Canvas unavailable');

      drawSocialCard(canvasCtx, img, ctxState.productTitle || panel.dataset.productTitle);

      const outputBlob = await new Promise((resolve, reject) => {
        try {
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Could not create social card')), 'image/png', 0.96);
        } catch (error) {
          reject(error);
        }
      });

      const filename = slugify(ctxState.productTitle || panel.dataset.productTitle) + '-mat-tryon.png';
      ctxState.socialCardBlob = outputBlob;
      ctxState.socialCardFile = new File([outputBlob], filename, { type: 'image/png' });

      button.disabled = false;
      button.textContent = 'Share social card';
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try social card again';
      throw error;
    }
  }

  function downloadSocialCard(ctxState) {
    const blob = ctxState?.socialCardBlob;
    const file = ctxState?.socialCardFile;
    if (!blob || !file) return;
    const outUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = outUrl;
    anchor.download = file.name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(outUrl), 1500);
  }

  function createSocialCard(panel) {
    const ctxState = state.get(panel);
    const button = panel.querySelector('[data-mat-social]');
    if (!ctxState || !button) return;

    if (!ctxState.socialCardFile) {
      prepareSocialCard(panel).catch(() => {
        button.disabled = false;
        button.textContent = 'Try social card again';
      });
      return;
    }

    const sharePayload = {
      files: [ctxState.socialCardFile],
      title: ctxState.productTitle || 'MAT Try On',
      text: 'MAT Try On / Seen on you',
    };

    if (navigator.share && navigator.canShare?.({ files: sharePayload.files })) {
      navigator.share(sharePayload).then(() => {
        trackMatEvent(panel, 'social_card_share');
      }).catch((error) => {
        if (error?.name !== 'AbortError') {
          downloadSocialCard(ctxState);
          trackMatEvent(panel, 'social_card_share');
        }
      });
      return;
    }

    downloadSocialCard(ctxState);
    trackMatEvent(panel, 'social_card_share');
  }

  async function submitLead(panel, event) {
    event.preventDefault();
    const ctx = state.get(panel);
    const email = panel.querySelector('[data-mat-email]')?.value.trim();
    if (!ctx || !email) return;
    try {
      await postJSON(panel.dataset.appUrl + '/api/lead', {
        shopDomain: panel.dataset.shopDomain,
        email,
        shopperHash: ctx.hash,
        consent: Boolean(panel.querySelector('[data-mat-marketing]')?.checked),
      });
      step(panel, 'upload');
    } catch (error) {
      showError(panel, error.message || 'Could not continue.');
    }
  }

  function showError(panel, message) {
    const target = panel.querySelector('[data-mat-error]');
    if (target) target.textContent = message;
    step(panel, 'error');
  }

  function openNativePanel(block) {
    const scope = block.closest('.shopify-section') || document;
    const panels = [...scope.querySelectorAll('[data-mat-panel]')];
    const panel = panels.find((p) => p.dataset.productId === block.dataset.productId) || panels[0];
    if (!panel) return false;

    pendingFitEntries.delete(panel.dataset.productId || '');
    const ctx = state.get(panel);
    if (ctx) {
      ctx.entryPoint = 'pdp';
      ctx.fitSeed = undefined;
      ctx.fitProfile = undefined;
      step(panel, 'consent');
      trackMatEvent(panel, 'widget_open');
    }

    const details = panel.closest('details[data-testid="accordion-details"]');
    if (!details) return false;

    const panelButton = details.querySelector('.sg04-panel-open');
    if (panelButton) {
      panelButton.click();
      requestAnimationFrame(() => panel.querySelector('button, input, label')?.focus?.({ preventScroll: true }));
      return true;
    }

    const summary = details.querySelector(':scope > summary');
    if (summary && !details.open) summary.click();
    else details.open = true;
    panel.scrollIntoView({ block: 'nearest' });
    return true;
  }

  function trackMatAddToCart() {
    const last = window.__mat04LastSuccessfulTryOn;
    if (!last || last.tracked || !last.panel?.isConnected) return;
    last.tracked = true;
    trackMatEvent(last.panel, 'add_to_cart_after_tryon');
  }

  function boot() {
    document.querySelectorAll('[data-mat-tryon]').forEach(hydrateLauncher);
    document.querySelectorAll('[data-mat-panel]').forEach(hydratePanel);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mat-open]');
    if (!trigger) return;
    const block = trigger.closest('[data-mat-tryon]');
    if (!block) return;
    event.preventDefault();
    openNativePanel(block);
  });

  window.addEventListener('mat:fit-entry', (event) => {
    const detail = event.detail || {};
    const productId = String(detail.productId || '');
    if (!productId) return;

    pendingFitEntries.set(productId, detail);
    document.querySelectorAll('[data-mat-panel]').forEach((panel) => {
      if (panel.dataset.productId === productId) applyFitEntry(panel, detail);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = String(form.getAttribute('action') || '');
    if (/\/cart\/add/i.test(action) || form.querySelector('[name="add"]')) trackMatAddToCart();
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[name="add"], [data-add-to-cart]');
    if (button) trackMatAddToCart();
  }, true);

  document.addEventListener('shopify:section:load', boot);
})();
